import type { Schemas } from '@argzombie/api-client';

/** Filters for `GET /admin/purchases` and `/admin/subscriptions`. An empty string means "any". */
export interface StoreFilters {
  playerId?: string;
  platform: string;
  environment: string;
  status: string;
  /** Purchases only: the content product kind (`consumable`, `subscription`, `resurrection`; ADR 0069). */
  productKind?: string;
}

export const PAGE_SIZE = 50;

/** Store environments, as the API records them. Anything but production is a test purchase. */
export const environments = ['production', 'sandbox', 'test', 'simulated'] as const;
export const platforms = ['apple', 'google', 'simulated'] as const;
export const purchaseStatuses = ['granted', 'active', 'expired', 'refunded', 'revoked'] as const;
export const subscriptionStatuses = ['active', 'grace', 'expired', 'revoked'] as const;

/**
 * A purchase row with ADR 0069's character: a resurrection credit's spend (`runId`, `slot`, `characterName`) and the
 * product's `kind`/`tier`.
 */
export type PurchaseRow = Schemas['AdminPurchaseRow'];

/** The Character cell of a purchase: who a spent resurrection credit brought back. */
export function purchaseCharacter(p: PurchaseRow): string {
  if (p.runId || p.slot != null || p.characterName) {
    const name =
      p.characterName?.trim() || (p.slot != null ? `Character ${p.slot}` : 'a character');
    const slot = p.slot != null && p.characterName ? ` (slot ${p.slot})` : '';
    return `${name}${slot}`;
  }
  return p.productKind === 'resurrection' ? 'unspent credit' : '-';
}

const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The query string for a page. Empty filters are left out. */
export function storeQuery(filters: StoreFilters, offset: number, take: number) {
  return {
    // The API binds playerId as a Guid; a half-typed id filters nothing rather than failing.
    playerId: filters.playerId && guid.test(filters.playerId) ? filters.playerId : undefined,
    platform: filters.platform || undefined,
    environment: filters.environment || undefined,
    status: filters.status || undefined,
    // ADR 0069: filter by product kind (`resurrection`, ...).
    productKind: filters.productKind || undefined,
    offset,
    take,
  };
}

/** Items in a pack as `id ×n`, comma separated. Since ADR 0055 a pack holds raw materials only (plus the kit while it raids). */
export function packSummary(pack: Schemas['TeamPackView']) {
  if (pack.items.length === 0) return 'empty';
  return pack.items.map((s) => `${s.itemId} ×${s.quantity}`).join(', ');
}

/**
 * A squad raid's summary for the raids table (Phase 3b telemetry): "Squad 4 · Orders 5 · AI kills 31%". Each part only when the
 * row has it (a solo raid, an older client or a raid without kills leaves it out), so a plain solo raid is blank.
 */
export function squadSummary(
  row: Pick<Schemas['AdminRaidRow'], 'squadSize' | 'orderCount' | 'aiKillShare'>,
): string {
  const parts: string[] = [];
  if ((row.squadSize ?? 0) > 0) parts.push(`Squad ${row.squadSize}`);
  if (row.orderCount != null) parts.push(`Orders ${row.orderCount}`);
  if (row.aiKillShare != null) parts.push(`AI kills ${Math.round(row.aiKillShare * 100)}%`);
  return parts.join(' · ');
}

/** A check-in's outcome (ADR 0030): "counted", or why not ("too soon", "out of range", "implausible"). */
export const checkInOutcome = (c: { counted: boolean; reason: string | null }) =>
  c.counted ? 'counted' : (c.reason ?? 'not counted').replaceAll('_', ' ');
type ConditionRow = Schemas['AdminConditionRow'];

/** The player detail's conditions (ADR 0031): active ones first (soonest end first, bites by turning time), then history, newest first. */
export function sortConditions(rows: ConditionRow[] | null | undefined): ConditionRow[] {
  const t = (iso: string | null | undefined) =>
    iso ? new Date(iso).getTime() : Number.MAX_SAFE_INTEGER;
  const active = (rows ?? []).filter((r) => r.resolvedAt == null);
  const done = (rows ?? []).filter((r) => r.resolvedAt != null);
  active.sort((a, b) => t(a.endsAt ?? a.turnsAt) - t(b.endsAt ?? b.turnsAt));
  done.sort((a, b) => t(b.resolvedAt) - t(a.resolvedAt));
  return [...active, ...done];
}

/** "in 2 h 10 m", "in 3 d 4 h", "now" (past), or '' for none. */
export function countdown(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return 'now';
  const mins = Math.ceil(ms / 60_000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `in ${d} d ${h} h`;
  if (h > 0) return `in ${h} h ${m} m`;
  return `in ${m} m`;
}

/**
 * "12.5/40 kg", or "12.5 kg" when the capacity is null (ADR 0048: with the limits off the API sends no capacity, meaning no limit).
 */
export function kgLabel(kg: number, capacityKg: number | null | undefined): string {
  return capacityKg == null ? `${kg} kg` : `${kg}/${capacityKg} kg`;
}

// ---- T49: the shared goods pool (ADR 0055) ----

/**
 * One equipped slot reference: the API's `InventoryResponse.inUse[]` (shared-cs `InUseView`). Typed here so the admin builds
 * before `@argzombie/api-client` has the T49 fields; the generated `Schemas['InUseView']` has the same shape.
 */
export interface InUseRow {
  itemId?: string;
  /** "player" or the survivor id. */
  memberId?: string;
  name?: string;
  /** The holder's team, or null on the bench. */
  teamId?: string | null;
  teamSlot?: number | null;
  /** weapon, weapon2 or armor. */
  slot?: string;
  /** The holder's team is in the open raid (the item is in the raid's kit). */
  inRaid?: boolean;
}

/** The T49 pool fields of the player detail's inventory (absent from an older API: then nothing is in use). */
export function poolOf(inventory: unknown): { inUse: InUseRow[]; kitTeamId: string | null } {
  const inv = (inventory ?? {}) as { inUse?: InUseRow[] | null; kitTeamId?: string | null };
  return { inUse: inv.inUse ?? [], kitTeamId: inv.kitTeamId ?? null };
}

/**
 * Who wears what, one line per holder, for a team (`teamId`) or the bench (`null`): "Mara: M9, Machete (2nd), Riot vest
 * (armor)". Holders keep the API's order (members in roster order, slots weapon, weapon2, armor).
 */
export function gearInUse(
  inUse: InUseRow[] | null | undefined,
  teamId: string | null,
  itemName: (id: string) => string = (id) => id,
): string[] {
  const byHolder = new Map<string, { name: string; items: string[] }>();
  for (const u of inUse ?? []) {
    if (!u.itemId || (u.teamId ?? null) !== teamId) continue;
    const key = u.memberId ?? u.name ?? '?';
    const holder = byHolder.get(key) ?? { name: u.name || key, items: [] };
    const tag = u.slot === 'weapon2' ? ' (2nd)' : u.slot === 'armor' ? ' (armor)' : '';
    holder.items.push(`${itemName(u.itemId)}${tag}`);
    byHolder.set(key, holder);
  }
  return [...byHolder.values()].map((h) => `${h.name}: ${h.items.join(', ')}`);
}

/** The fields of an item the grant form reads (published or draft `ItemDef`). */
export interface GrantItem {
  id?: string | null;
  name?: string | null;
  category?: string | null;
  mobility?: string | null;
  tags?: string[] | null;
  armor?: {
    reduction?: number | null;
    bonusHealth?: number | null;
    blocksPerRaid?: number | null;
    durability?: number | null;
  } | null;
}

export interface GrantSurvival {
  foodItemIds?: string[] | null;
  waterItemIds?: string[] | null;
}

const GOODS_CATEGORIES = ['weapon', 'ammo', 'armor', 'backpack', 'consumable', 'throwable', 'tool'];

/**
 * Goods ("loadout") or raw materials ("haul"), as shared-cs `ItemMobility.Of`: the item's own `mobility`, then survival food and
 * water are haul, then the `medicine` tag is goods, then the category default. An unknown item is haul.
 */
export function itemMobility(
  item: GrantItem | null | undefined,
  survival?: GrantSurvival | null,
): 'loadout' | 'haul' {
  if (!item) return 'haul';
  if (item.mobility === 'loadout' || item.mobility === 'haul') return item.mobility;
  const id = item.id ?? '';
  if (survival?.foodItemIds?.includes(id) || survival?.waterItemIds?.includes(id)) return 'haul';
  if (item.tags?.includes('medicine')) return 'loadout';
  return GOODS_CATEGORIES.includes(item.category ?? '') ? 'loadout' : 'haul';
}

/**
 * Where an admin grant really lands (ADR 0055; `AdminEndpoints.Grant`): a pack (`carried` or `team:{id}`) only holds raw
 * materials, so a positive grant of goods to a pack goes to the shared pool (`base`). Everything else lands where it was sent.
 */
export function grantLandsIn(
  location: string,
  item: GrantItem | null | undefined,
  quantity: number,
  survival?: GrantSurvival | null,
): string {
  const pack = location === 'carried' || location.startsWith('team:');
  return pack && quantity > 0 && itemMobility(item, survival) === 'loadout' ? 'base' : location;
}

/** The grant form's quick-pick filter: every item, or armor only. */
export type GrantFilter = 'all' | 'armor';

/** "Flak vest (kevlar_vest) · 15% · 3 blocks · 30 pad · 260 dur"; the reduction only when the piece has one. */
export function grantPickLabel(item: GrantItem): string {
  const base = `${item.name || item.id} (${item.id})`;
  const a = item.armor;
  if (!a) return base;
  const parts = [base];
  if ((a.reduction ?? 0) > 0) parts.push(`${Math.round((a.reduction ?? 0) * 100)}%`);
  parts.push(
    `${a.blocksPerRaid ?? 0} blocks`,
    `${a.bonusHealth ?? 0} pad`,
    `${a.durability ?? 0} dur`,
  );
  return parts.join(' · ');
}

/**
 * The grant form's pick list: every item by name, or (armor) the items with an armor block in the armor category, weakest first
 * (reduction, then durability, then id), so the dev-only hard-plate tiers (T49) sit at the end.
 */
export function grantPicks(
  items: GrantItem[] | null | undefined,
  filter: GrantFilter,
): { id: string; label: string }[] {
  const list = (items ?? []).filter((i): i is GrantItem & { id: string } => !!i?.id);
  if (filter === 'armor') {
    return list
      .filter((i) => i.category === 'armor' && i.armor)
      .sort(
        (a, b) =>
          (a.armor?.reduction ?? 0) - (b.armor?.reduction ?? 0) ||
          (a.armor?.durability ?? 0) - (b.armor?.durability ?? 0) ||
          a.id.localeCompare(b.id),
      )
      .map((i) => ({ id: i.id, label: grantPickLabel(i) }));
  }
  return list
    .slice()
    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    .map((i) => ({ id: i.id, label: `${i.name || i.id} (${i.id})` }));
}

/** A member's rest state (ADR 0073 §7.4) as one short line: "WINDED, 1.5 h to the next tier, wounded, bitten", or "RESTED". */
export function restLabel(rest: Schemas['RestStateView'] | null | undefined): string {
  if (!rest) return '';
  const parts = [rest.label];
  if (rest.hoursToNextTier != null) parts.push(`${Number(rest.hoursToNextTier).toFixed(1)} h to the next tier`);
  if (rest.debt > 0) parts.push(`debt ${Number(rest.debt).toFixed(2)} winds`);
  if (rest.wounded) parts.push('wounded');
  if (rest.bitten) parts.push('bitten');
  return parts.join(', ');
}
